import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import {
  CalendarEventFormData,
  GoogleCalendarEventDateTime,
  GoogleCalendarEventRaw,
} from './Calendar.type';
import { Attachment } from '../Attachment/Attachment.type';

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

const hasTimezoneOffset = (value: string): boolean =>
  /([zZ]|[+-]\d{2}:?\d{2})$/.test(value.trim());

const RESUME_TYPE = 'RESUME';

export const formatDateTimeForApi = (timestampMs: number): string => {
  return dayjs(timestampMs).utc().toISOString();
};

export const getAttachmentFileName = (url?: string): string => {
  if (!url) return '';
  const withoutQuery = url.split('?')[0];
  const name = withoutQuery.substring(withoutQuery.lastIndexOf('/') + 1);
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
};

export type CalendarCv = Pick<
  Attachment,
  'uuid' | 'type' | 'url' | 'isCloud' | 'isPrivate'
> &
  Partial<Pick<Attachment, 'createdAt' | 'comment'>>;

export const getCloudCvs = (
  personAttachments: CalendarCv[] | null | undefined = [],
): CalendarCv[] => {
  const byUuid = new Map<string, CalendarCv>();

  (personAttachments ?? []).forEach(attachment => {
    if (attachment.uuid) byUuid.set(attachment.uuid, attachment);
  });

  return Array.from(byUuid.values())
    .filter(
      attachment =>
        attachment.type === RESUME_TYPE &&
        attachment.isCloud &&
        !attachment.isPrivate,
    )
    .sort((a, b) => {
      const timestamp = (value: CalendarCv['createdAt']) => {
        if (!value) return 0;
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.valueOf() : 0;
      };
      return timestamp(b.createdAt) - timestamp(a.createdAt);
    });
};

export const formatDisplayDateTime = (
  value?: string,
  format = 'DD.MM.YYYY HH:mm',
): string => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(format) : '';
};

export const parseGoogleDateTime = (
  value?: string | GoogleCalendarEventDateTime,
): { iso: string; timeZone?: string } => {
  if (!value) return { iso: '' };

  if (typeof value === 'string') {
    const parsed = dayjs(value);
    return { iso: parsed.isValid() ? parsed.toISOString() : '' };
  }

  if (value.dateTime && value.timeZone && !hasTimezoneOffset(value.dateTime)) {
    const parsed = dayjs.tz(value.dateTime, value.timeZone);
    return {
      iso: parsed.isValid() ? parsed.toISOString() : '',
      timeZone: value.timeZone,
    };
  }

  if (value.dateTime) {
    const parsed = dayjs(value.dateTime);
    return {
      iso: parsed.isValid() ? parsed.toISOString() : '',
      timeZone: value.timeZone,
    };
  }

  if (value.date) {
    const parsed = dayjs(value.date);
    return {
      iso: parsed.isValid() ? parsed.toISOString() : '',
      timeZone: value.timeZone,
    };
  }

  return { iso: '', timeZone: value.timeZone };
};

export const mapGoogleCalendarEventToFormData = (
  raw: GoogleCalendarEventRaw,
): CalendarEventFormData => {
  const start = parseGoogleDateTime(raw.start_time);
  const end = parseGoogleDateTime(raw.end_time);

  return {
    title: raw.summary || '',
    description: raw.description || '',
    startTime: start.iso,
    endTime: end.iso,
    timeZone: start.timeZone || end.timeZone,
    meetLink: raw.meet_link,
    participants: (raw.participants || [])
      .map(participant => ({ email: participant.email || '' }))
      .filter(participant => !!participant.email),
  };
};
